import { ButtonGroup, PrimaryButton } from "@digipost/dds-buttons-react";
import { Heading } from "@digipost/dds-heading-react";
import Infobox from "@digipost/dds-infobox-react";
import { FC, ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate } from "react-router";

import { RegistrableOrganisation } from "../../../../types/registration";
import InfoWarningText from "../../InfoWarningText";
import { Dispatch } from "../../infrastructure/redux/actions";
import { setUserInputAction } from "../../infrastructure/redux/org/org-actions";
import { ReduxState } from "../../infrastructure/redux/state";
import Translated from "../../infrastructure/Translated";
import { useTranslate } from "../../infrastructure/useTranslate";
import { isAlreadyRegistered } from "../domain";
import OrganisationChoice from "../OrganisationChoice";
import styles from "./SelectOrganisationForm.module.css";

const SelectOrganisationForm: FC = () => {
  const dispatch = useDispatch<Dispatch>();
  const navigate = useNavigate();
  const translate = useTranslate();

  const registrableOrganisations = useSelector(
    (state: ReduxState) => state.org.data?.registrableOrganisations ?? [],
  );

  const integrationPartners = useSelector(
    (state: ReduxState) => state.org.data?.integrationPartners ?? [],
  );

  const selectedOrganisationNumber = useSelector(
    (state: ReduxState) => state.org.userInput.selectedOrganisationNumber,
  );

  const selectedOrganisation = registrableOrganisations.find(
    (o) => o.organisationNumber === selectedOrganisationNumber,
  );

  const { integrationType, mode, preselectedOrg, partnerId } = useSelector(
    (state: ReduxState) => state.app.params,
  );

  const erBehandlerFlyt = mode === "behandler";

  const canProceed: boolean =
    !!selectedOrganisation &&
    (!isAlreadyRegistered(selectedOrganisation) ||
      (erBehandlerFlyt && integrationType !== "2"));

  const next = (): void => {
    if (!canProceed) {
      return;
    }

    const nextPage =
      erBehandlerFlyt && integrationType !== "2" ? "fullmakt" : "avtalevilkaar";

    navigate(nextPage);
  };

  const [submitted, setSubmitted] = useState<boolean>(false);

  const partner = integrationPartners.find(
    (p) => partnerId && p.senderId === Number(partnerId),
  );

  const getWarnings = (
    registrableOrganisation: RegistrableOrganisation,
  ): ReactNode => {
    const isSelected =
      registrableOrganisation.organisationNumber === selectedOrganisationNumber;

    if (!isSelected || !isAlreadyRegistered(registrableOrganisation)) {
      return null;
    }

    if (!erBehandlerFlyt) {
      return (
        <InfoWarningText
          className={styles.warning}
          simple={true}
          textkey="register.org.registration_form.already_registered.ingress"
        >
          <ul className="extra-margin-bottom">
            <li>
              <Translated textKey="register.org.registration_form.already_registered.li1" />
            </li>
            <li>
              <Translated textKey="register.org.registration_form.already_registered.li2" />
            </li>
            <li>
              <Translated textKey="register.org.registration_form.already_registered.li3" />
              <a href="mailto:kundeservice@digipost.no">
                kundeservice@digipost.no
              </a>
            </li>
          </ul>
        </InfoWarningText>
      );
    }

    if (integrationType === "2") {
      return (
        <InfoWarningText
          className={styles.warning}
          simple={true}
          textkey="register.org.registration_form.already_registered_in_dp"
        />
      );
    }
  };

  if (partner && preselectedOrg) {
    const registrableOrganisation = registrableOrganisations.find(
      (o) => o.organisationNumber === preselectedOrg,
    );

    if (!registrableOrganisation) {
      return <Navigate to="../manuell" />;
    }

    return (
      <div className={styles.wrapper}>
        <div>
          <Heading level={1} size="large">
            <Translated textKey="select_org.title.no_choice" />
          </Heading>

          <OrganisationChoice
            name={registrableOrganisation.name}
            organisationNumber={registrableOrganisation.organisationNumber}
          >
            {getWarnings(registrableOrganisation)}
          </OrganisationChoice>

          <p>
            <Translated textKey="select_org.not_correct" />{" "}
            <Translated
              textKey="select_org.contact_partner"
              params={{ name: partner.name }}
            />
          </p>

          <ButtonGroup>
            <PrimaryButton type="button" onClick={next}>
              <Translated textKey="select_org.continue" />
            </PrimaryButton>
          </ButtonGroup>
        </div>
      </div>
    );
  }

  if (registrableOrganisations.length === 1) {
    const registrableOrganisation = registrableOrganisations[0];

    return (
      <div className={styles.wrapper}>
        <div>
          <Heading level={1} size="large">
            <Translated textKey="select_org.title.no_choice" />
          </Heading>
          <OrganisationChoice
            name={registrableOrganisation.name}
            organisationNumber={registrableOrganisation.organisationNumber}
          >
            {getWarnings(registrableOrganisation)}
          </OrganisationChoice>

          <p>
            <Translated textKey="select_org.not_correct" />{" "}
            <Link
              to="../manuell"
              data-testid="bedrift-manual-registration-link"
            >
              <Translated textKey="select_org.go_to_manual" />
            </Link>
          </p>

          {canProceed && (
            <ButtonGroup>
              <PrimaryButton
                type="button"
                data-testid="bedrift-choose-org-next-button"
                onClick={next}
              >
                <Translated textKey="select_org.continue" />
              </PrimaryButton>
            </ButtonGroup>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <Heading level={1} size="large">
          <Translated textKey="select_org.title.multiple_choice" />
        </Heading>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(true);
            next();
          }}
        >
          <div>
            <fieldset className={styles.radioGroup}>
              {registrableOrganisations.map((org) => (
                <OrganisationChoice
                  key={org.organisationNumber}
                  onChange={(orgNumber) =>
                    dispatch(
                      setUserInputAction({
                        selectedOrganisationNumber: orgNumber,
                      }),
                    )
                  }
                  isSelected={
                    org.organisationNumber === selectedOrganisationNumber
                  }
                  name={org.name}
                  organisationNumber={org.organisationNumber}
                >
                  {getWarnings(org)}
                </OrganisationChoice>
              ))}
            </fieldset>
          </div>

          <p>
            <Translated textKey="select_org.not_correct" />{" "}
            <Link
              to="../manuell"
              data-testid="bedrift-manual-registration-link"
            >
              <Translated textKey="select_org.go_to_manual" />
            </Link>
          </p>

          <div role="alert">
            {submitted && !selectedOrganisationNumber && (
              <Infobox
                title={translate("select_org.alert.none_selected")}
                variant="error"
                level={2}
              />
            )}
          </div>

          {canProceed && (
            <ButtonGroup>
              <PrimaryButton type="submit">
                <Translated textKey="select_org.continue" />
              </PrimaryButton>
            </ButtonGroup>
          )}
        </form>
      </div>
    </div>
  );
};

export default SelectOrganisationForm;
